import { Box, Avatar, Tooltip, Typography, AvatarGroup, avatarGroupClasses } from '@mui/material';

import { useFetchMediaUrlQuery } from 'src/pages/chat/slice/chatApi';

// ----------------------------------------------------------------------

type Assignee = {
  id: string;
  type: 'USER' | 'TEAM';
  name: string;
  ProfileURL?: string;
};

type RowCellAssigneeProps = {
  assignees: Assignee[];
  isSelectRecordDialog?: boolean;
  isSupportReport?: boolean;
  t: (key: string) => string;
};

// ----------------------------------------------------------------------

function AssigneeAvatar({
  assignee,
  getAvatarContent,
  getTooltipTitle,
  AVATAR_SIZE,
  AVATAR_FONT_SIZE,
}: {
  assignee: Assignee;
  getAvatarContent: (name?: string) => string;
  getTooltipTitle: (assignee: Assignee) => string;
  AVATAR_SIZE: number;
  AVATAR_FONT_SIZE: number;
}) {
  const shouldFetch =
    assignee.ProfileURL &&
    typeof assignee.ProfileURL === 'string' &&
    !assignee.ProfileURL.startsWith('http');

  const { data: fetchedUrl } = useFetchMediaUrlQuery(
    { fileName: assignee.ProfileURL },
    { skip: !shouldFetch }
  );

  const profileUrl = assignee.ProfileURL?.startsWith('http') ? assignee.ProfileURL : fetchedUrl;

  return (
    <Tooltip title={getTooltipTitle(assignee)}>
      <Avatar
        sx={{
          width: AVATAR_SIZE,
          height: AVATAR_SIZE,
          fontSize: AVATAR_FONT_SIZE,
        }}
        alt={assignee.name || ''}
        src={profileUrl || undefined}
      >
        {getAvatarContent(assignee.name)}
      </Avatar>
    </Tooltip>
  );
}

export function RowCellAssignee({
  assignees,
  isSelectRecordDialog = false,
  isSupportReport = false,
  t,
}: RowCellAssigneeProps) {
  // Constants
  const AVATAR_SIZE = 28;
  const AVATAR_FONT_SIZE = 12;
  const MAX_VISIBLE_AVATARS = 2;

  // Helper functions
  const getAvatarContent = (name?: string) => name?.charAt(0).toUpperCase() ?? '?';

  const getTooltipTitle = (assignee: Assignee) =>
    `${assignee.name || 'Unknown'} (${t(assignee.type) || 'Unknown'})`;

  const getHiddenAssigneesTooltip = (hiddenAssignees: Assignee[]) =>
    hiddenAssignees
      .map((assignee) => `${assignee.name} (${assignee.type?.toLowerCase() || 'unknown'})`)
      .join(', ');

  // Early return for empty state
  if (assignees.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Unassigned
      </Typography>
    );
  }

  // Simplified view for dialogs and reports
  if (isSelectRecordDialog || isSupportReport) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {assignees.map((assignee) => (
          <Typography key={assignee.id} variant="body2" noWrap>
            {assignee.name}
          </Typography>
        ))}
      </Box>
    );
  }

  // Main avatar group view
  const visibleAssignees = assignees.slice(0, MAX_VISIBLE_AVATARS);
  const hiddenAssignees = assignees.slice(MAX_VISIBLE_AVATARS);
  const hasHiddenAssignees = hiddenAssignees.length > 0;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
      {/* Main Avatar Group */}
      <AvatarGroup
        max={MAX_VISIBLE_AVATARS}
        sx={{
          [`& .${avatarGroupClasses.avatar}`]: {
            width: AVATAR_SIZE,
            height: AVATAR_SIZE,
            fontSize: AVATAR_FONT_SIZE,
            cursor: 'pointer',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              transform: 'scale(1.1)',
              boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
              zIndex: 1,
            },
          },
        }}
      >
        {visibleAssignees.map((assignee) => (
          <AssigneeAvatar
            key={assignee.id}
            assignee={assignee}
            getAvatarContent={getAvatarContent}
            getTooltipTitle={getTooltipTitle}
            AVATAR_SIZE={AVATAR_SIZE}
            AVATAR_FONT_SIZE={AVATAR_FONT_SIZE}
          />
        ))}
      </AvatarGroup>

      {/* +X Avatar with tooltip showing all hidden users */}
      {hasHiddenAssignees && (
        <Tooltip title={getHiddenAssigneesTooltip(hiddenAssignees)}>
          <Avatar
            sx={{
              width: AVATAR_SIZE,
              height: AVATAR_SIZE,
              fontSize: AVATAR_FONT_SIZE,
              cursor: 'pointer',
              backgroundColor: 'grey.100',
              color: 'grey.600',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                backgroundColor: 'grey.200',
                transform: 'scale(1.1)',
                boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
              },
            }}
          >
            +{hiddenAssignees.length}
          </Avatar>
        </Tooltip>
      )}
    </Box>
  );
}
